(() => {
  const matchesSelection = (product, selected, sample) => product.panelVerified !== false && selected.every((id) => product.subs.includes(id)) && (sample === 'all' || product.sample === sample);
  const nextComparison = (current, id) => current.includes(id) ? current.filter((value) => value !== id) : current.length < 3 ? [...current, id] : current;
  const escapeHtml = (value) => String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

  const unitPrice = (payload, variantId) => {
    const variant = Array.isArray(payload?.variants) ? payload.variants.find((item) => item && String(item.id) === String(variantId)) : null;
    return variant && Number.isSafeInteger(variant.price) && variant.price >= 0 ? variant.price : null;
  };
  const productJsonPath = (link, localeRoot = '/') => {
    if (!/^\/products\/[a-z0-9][a-z0-9-]*$/.test(link) || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/i.test(localeRoot)) return null;
    return localeRoot + link.slice(1) + '.js';
  };
  const loadUnitProduct = (path, fetchFn = fetch, timeoutMs = 6000) => {
    const controller = new AbortController();
    let timeout;
    const expired = new Promise((resolve) => {
      timeout = setTimeout(() => { controller.abort(); resolve(null); }, timeoutMs);
    });
    const request = Promise.resolve().then(() => fetchFn(path, { credentials: 'same-origin', mode: 'same-origin', redirect: 'error', signal: controller.signal, headers: { Accept: 'application/json' } }))
      .then((response) => { if (!response.ok) throw new Error('product unavailable'); return response.json(); });
    return Promise.race([request, expired]).catch(() => null).finally(() => clearTimeout(timeout));
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { matchesSelection, nextComparison, escapeHtml, unitPrice, productJsonPath, loadUnitProduct };
  if (typeof document === 'undefined') return;
  const initialize = () => {
    const root = document.querySelector('#amaGuideAchat');
    if (!root || root.dataset.guideReady) return;
    const data = root.querySelector('[data-ama-guide-data]');
    if (!data) return;
    let config;
    try { config = JSON.parse(data.textContent); } catch (_) { return; }
    root.dataset.guideReady = 'true';
    const { substances, products, strings, details = {}, detailStrings: ds = {} } = config;
    const priceRequests = new Map();
    const byId = new Map(substances.map((item) => [item.id, item]));
    const productByLink = new Map(products.map((item) => [item.link, item]));
    const get = (id) => root.querySelector('#' + id);
    let selected = [];
    let sample = 'all';
    let comparison = [];
    const productLink = (product) => productJsonPath(product.link, window.Shopify?.routes?.root || '/')?.slice(0, -3) || '';
    const panelText = (product) => product.panelLabel || product.subs.map((id) => byId.get(id)?.sub).filter(Boolean).join(' · ');

    const detail = (product) => details[product.link] || {};
    const noticeLink = (url) => {
      try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' && parsed.hostname === 'cdn.shopify.com' && /\.pdf$/i.test(parsed.pathname) ? parsed.href : '';
      } catch (_) { return ''; }
    };
    const formatDetail = (product, kind) => {
      const d = detail(product);
      if (kind === 'format') return escapeHtml(ds[d.format] || product.type);
      if (kind === 'reading') return escapeHtml(d.readMinutes ? ds.read_at.replace('[minutes]', d.readMinutes).replace('[limit]', d.readLimit) : ds.details_missing);
      if (kind === 'thresholds') return d.thresholds?.length ? d.thresholds.map(([code, value]) => escapeHtml(code + ' ' + value)).join('<br>') : escapeHtml(d.noticeUrl ? ds.thresholds_missing : ds.details_missing);
      if (kind === 'packaging') return escapeHtml(d.unitVariantId ? ds.unit_sale : ds.details_missing);
      if (kind === 'unit_price') return d.unitVariantId ? `<span data-ama-unit-price="${escapeHtml(product.link)}">${escapeHtml(ds.price_loading)}</span>` : escapeHtml(ds.price_missing);
      const url = noticeLink(d.noticeUrl);
      return url ? `<a href="${escapeHtml(url)}" target="_blank" rel="noopener">${escapeHtml(ds.notice)}</a>` : escapeHtml(ds.details_missing);
    };
    const hydratePrices = () => root.querySelectorAll('[data-ama-unit-price]').forEach((cell) => {
      const link = cell.dataset.amaUnitPrice;
      const d = details[link];
      const path = productJsonPath(link, window.Shopify?.routes?.root || '/');
      if (!path || !d?.unitVariantId) { cell.textContent = ds.price_missing; return; }
      if (!priceRequests.has(path)) {
        priceRequests.set(path, loadUnitProduct(path).then((payload) => {
          if (!payload) priceRequests.delete(path);
          return payload;
        }));
      }
      priceRequests.get(path).then((payload) => {
        if (!cell.isConnected) return;
        const cents = unitPrice(payload, d.unitVariantId);
        try {
          cell.textContent = cents === null ? ds.price_missing : new Intl.NumberFormat(config.locale || 'fr', { style: 'currency', currency: config.currency }).format(cents / 100);
        } catch (_) { cell.textContent = ds.price_missing; }
      });
    });
    const renderCompareButtons = () => root.querySelectorAll('[data-ama-compare]').forEach((button) => {
      const active = comparison.includes(button.dataset.amaCompare);
      button.textContent = active ? strings.compared : strings.compare;
      button.setAttribute('aria-pressed', String(active));
      button.disabled = comparison.length === 3 && !active;
    });
    const renderComparison = () => {
      const region = get('AmaGuideComparison');
      region.hidden = comparison.length === 0;
      get('AmaGuideCompareStatus').textContent = comparison.length === 3 ? strings.compare_limit : strings.compare_hint;
      if (!comparison.length) { get('AmaGuideCompareTable').innerHTML = ''; return; }
      const chosen = comparison.map((id) => productByLink.get(id)).filter(Boolean);
      get('AmaGuideCompareTable').innerHTML = `<div class="ama-guide-compare__scroll" tabindex="0" role="region" aria-label="${escapeHtml(strings.compare_title)}"><table><thead><tr><th scope="col">${escapeHtml(strings.criterion)}</th>${chosen.map((p) => `<th scope="col"><span>${escapeHtml(p.name)}</span><button type="button" data-ama-remove="${escapeHtml(p.link)}" aria-label="${escapeHtml(strings.remove + ' ' + p.name)}">${escapeHtml(strings.remove)}</button></th>`).join('')}</tr></thead><tbody><tr><th scope="row">${escapeHtml(strings.type)}</th>${chosen.map((p) => `<td>${escapeHtml(p.type)}</td>`).join('')}</tr><tr><th scope="row">${escapeHtml(strings.panel)}</th>${chosen.map((p) => `<td>${escapeHtml(panelText(p))}</td>`).join('')}</tr>${["format", "reading", "thresholds", "packaging", "unit_price", "notice"].map((kind) => `<tr><th scope="row">${escapeHtml(ds[kind])}</th>${chosen.map((p) => `<td>${formatDetail(p, kind)}</td>`).join('')}</tr>`).join('')}<tr><th scope="row">${escapeHtml(strings.product_page)}</th>${chosen.map((p) => `<td><a href="${escapeHtml(productLink(p))}">${escapeHtml(strings.view_product)}</a></td>`).join('')}</tr></tbody></table></div>`;
      hydratePrices();
    };
    const renderUnverified = () => {
      const section = get('AmaGuideUnverified');
      const candidates = products.filter((p) => p.panelVerified === false && (sample === 'all' || p.sample === sample));
      section.hidden = !candidates.length || (!selected.length && sample === 'all');
      get('AmaGuideUnverifiedLinks').innerHTML = candidates.map((p) => `<a href="${escapeHtml(productLink(p))}">${escapeHtml(p.name)}<span aria-hidden="true"> →</span></a>`).join('');
      get('AmaGuideProtection').hidden = sample !== 'other' && !selected.includes('ghb') && !selected.includes('ket');
    };
    const updateResults = () => {
      const summary = get('selectedSummary');
      summary.innerHTML = selected.map((id) => { const s = byId.get(id); return `<span class="ama-selected-pill">${escapeHtml(s.sub)} · ${escapeHtml(s.label)}</span>`; }).join('');
      summary.classList.toggle('visible', selected.length > 0);
      const active = selected.length > 0 || sample !== 'all';
      get('emptyState').hidden = active;
      if (!active) {
        get('prodGrid').innerHTML = '';
        get('resultCount').textContent = strings.no_selection;
        renderUnverified();
        return;
      }
      const matching = products.filter((p) => matchesSelection(p, selected, sample));
      get('resultCount').textContent = (matching.length === 1 ? strings.result_one : strings.result_count).replace('[count]', matching.length);
      get('prodGrid').innerHTML = matching.length ? matching.map((p) => `<article class="ama-prod-row"><a href="${escapeHtml(productLink(p))}" class="ama-prod-main"><span class="ama-prod-type">${escapeHtml(p.type)}</span><h3 class="ama-prod-title">${escapeHtml(p.name)}</h3><p class="ama-prod-desc">${escapeHtml(p.desc)}</p></a><div class="ama-prod-side"><div class="ama-prod-matches">${(p.panelGroups || p.subs.map((id) => [id])).map((ids) => `<span class="ama-match-pill ${ids.some((id) => selected.includes(id)) ? 'active' : ''}">${escapeHtml(ids.map((id) => byId.get(id)?.sub).filter(Boolean).join('/'))}</span>`).join('')}</div><a class="ama-prod-btn" href="${escapeHtml(productLink(p))}">${escapeHtml(strings.view_product)}</a><button class="ama-guide-compare-toggle" type="button" data-ama-compare="${escapeHtml(p.link)}" aria-pressed="false" aria-label="${escapeHtml(strings.compare + ' ' + p.name)}">${escapeHtml(strings.compare)}</button></div></article>`).join('') : `<div class="ama-no-result-state">${escapeHtml(strings.no_result)}</div>`;
      renderCompareButtons();
      renderUnverified();
    };
    ['antidouleurs', 'illicites', 'soumission', 'nps'].forEach((category) => {
      get('grid-' + category).innerHTML = substances.filter((item) => item.cat === category).map((s) => `<button type="button" class="sub-btn" id="btn-${escapeHtml(s.id)}" aria-pressed="false" data-substance="${escapeHtml(s.id)}"><span class="sub-symbol" aria-hidden="true">${escapeHtml(s.sub)}</span><span class="sub-label">${escapeHtml(s.label)}</span><span class="sub-abduction">${escapeHtml(s.sub)}</span><span class="sub-cat">${escapeHtml(s.desc)}</span></button>`).join('');
    });
    root.addEventListener('click', (event) => {
      const substance = event.target.closest('[data-substance]');
      const filter = event.target.closest('[data-ama-sample]');
      const compare = event.target.closest('[data-ama-compare]');
      const remove = event.target.closest('[data-ama-remove]');
      if (substance) {
        const id = substance.dataset.substance;
        selected = selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id];
        substance.classList.toggle('active', selected.includes(id));
        substance.setAttribute('aria-pressed', String(selected.includes(id)));
        updateResults();
      } else if (filter) {
        sample = filter.dataset.amaSample;
        root.querySelectorAll('[data-ama-sample]').forEach((button) => button.setAttribute('aria-pressed', String(button === filter)));
        updateResults();
      } else if (compare || remove) {
        const link = compare?.dataset.amaCompare || remove.dataset.amaRemove;
        comparison = nextComparison(comparison, link);
        renderComparison();
        renderCompareButtons();
        if (remove) {
          const target = comparison.length
            ? get('AmaGuideComparisonTitle')
            : [...root.querySelectorAll('[data-ama-compare]')]
              .find((button) => button.dataset.amaCompare === link)
              || root.querySelector('[data-ama-sample][aria-pressed="true"]');
          target?.focus({ preventScroll: comparison.length > 0 });
        }
      }
    });
    const params = new URLSearchParams(window.location.search);
    selected = [...new Set((params.get('substances') || params.get('substance') || params.get('drug') || '').split(',').map((value) => value.trim().toLowerCase()).filter((id) => byId.has(id)))];
    if (['saliva', 'urine', 'other'].includes(params.get('sample'))) sample = params.get('sample');
    selected.forEach((id) => { const button = get('btn-' + id); button.classList.add('active'); button.setAttribute('aria-pressed', 'true'); });
    root.querySelectorAll('[data-ama-sample]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.amaSample === sample)));
    updateResults();
    if (selected.length) requestAnimationFrame(() => get('AmaGuideResults').scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
